import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { MayHaveLabel } from '@nivo/pie';
import { IconArrowsMaximize, IconArrowsMinimize } from '@tabler/icons-react';
import { PoolSummaryPerformanceSummary } from 'features/loanPool/__generated__/PoolSummaryPerformanceSummary';
import { ModalNotStyled } from 'ui-kit';

import { AssetClass } from '__generated__/globalTypes';

import { StratificationsPerformanceSummary } from './__generated__/StratificationsPerformanceSummary';
import { stratificationsConfig, StratKey, StratRowType } from './startifications.config';
import { StratChart } from './StratChart';
import { StratTable } from './StratTable';
import { ToggleButton, IconButton } from '../../../ui-kit';
import { ToggleChartTableBtnGroup } from '../../pages/marketplace/ActiveTrades/ToggleChartTableBtnGroup';

export type StratCardContentProps = {
  data: (StratRowType & MayHaveLabel)[];
  config: (typeof stratificationsConfig)[StratKey];
  strat: StratKey;
  assetClass: AssetClass;
  summary: StratificationsPerformanceSummary;
  avgSummary: PoolSummaryPerformanceSummary;
  height: string;
  width: string;
  mode?: 'card' | 'modal';
};

export function StratCard({
  strat,
  assetClass,
  summary,
  avgSummary,
  mode = 'card',
  displayType = 'chart',
  onMinimizeClick,
  'data-tour': dataTour,
}: {
  strat: StratKey;
  assetClass: AssetClass;
  summary: StratificationsPerformanceSummary;
  avgSummary: PoolSummaryPerformanceSummary;
  mode?: 'card' | 'modal';
  displayType?: 'chart' | 'table';
  onMinimizeClick?: () => void;
  'data-tour'?: string;
}) {
  const [isChart, setIsChart] = useState<boolean>(displayType === 'chart');
  const [isPieChart, setIsPieChart] = useState<boolean>(true);
  const [containerHeight, setContainerHeight] = useState('0px');
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (mode !== 'modal' && el) {
      const resizeObserver = new ResizeObserver(() => {
        const measured = el.offsetHeight;
        setContainerHeight(`${measured - 80}px`);
      });

      resizeObserver.observe(el);

      // Initial set
      setContainerHeight(`${el.offsetHeight - 80}px`);

      return () => resizeObserver.disconnect();
    }
  }, [mode]);

  // Use v2 configuration with enhanced charts
  const cfg = stratificationsConfig[strat];
  if (!cfg) {
    return null;
  }
  const config: NonNullable<(typeof stratificationsConfig)[StratKey]> = {
    ...cfg,
    chart:
      // Toggle between chart types for Original Balance
      cfg.label === 'Original Balance' ? (isPieChart ? 'Pie' : 'Bar_Vertical_Dots') : cfg.chart,
  };

  const contentProps: StratCardContentProps = useMemo(
    () => ({
      data: summary[strat] as StratCardContentProps['data'],
      config: config,
      strat,
      assetClass,
      summary,
      avgSummary,
      height: mode === 'modal' ? `${window.innerHeight * 0.7}px` : containerHeight,
      width: '100%',
    }),
    [summary, strat, config, assetClass, avgSummary, mode, containerHeight],
  );
  const handleToggle = (state: boolean) => {
    setIsPieChart(state);
  };

  return (
    <div
      id="containerWithRef"
      ref={containerRef}
      data-tour={dataTour}
      data-testid={`strat-card-${strat}`}
      className={`relative flex flex-col !overflow-hidden rounded-md border border-accent-disabled bg-background-surface p-4 hover:border-accent-muted ${
        mode === 'modal' ? 'max-h-[80vh] w-[calc(100vw-40px)]' : 'w-auto overflow-hidden'
      } relative aspect-[16/10]`}
    >
      <div className={`mb-4 flex items-center justify-between ${mode === 'modal' ? 'pr-5' : ''}`}>
        <span className="font-heebo text-xl font-semibold text-gray-400">{config.label}</span>
        <div className="flex items-center gap-4">
          <ToggleChartTableBtnGroup isChart={isChart} setIsChart={setIsChart} />

          {mode !== 'modal' ? (
            <ModalNotStyled
              trigger={
                <IconButton
                  variant="icon-only"
                  icon={<IconArrowsMaximize size={16} color="#e85ee5" />}
                  className="rounded-full p-2"
                  onClick={() => {}}
                />
              }
            >
              {({ closeModal }) => (
                <StratCard
                  strat={strat}
                  assetClass={assetClass}
                  summary={summary}
                  avgSummary={avgSummary}
                  mode="modal"
                  displayType={isChart ? 'chart' : 'table'}
                  onMinimizeClick={closeModal}
                />
              )}
            </ModalNotStyled>
          ) : (
            <IconButton
              variant="icon-only"
              icon={<IconArrowsMinimize size={16} color="#e85ee5" />}
              className="rounded-full p-3"
              onClick={onMinimizeClick}
            />
          )}
        </div>
        {config.label === 'Original Balance' && (
          <div className="absolute bottom-4 left-4 z-[2]">
            <ToggleButton
              initialState={isPieChart}
              checkedLabel="Pie"
              uncheckedLabel="Bar"
              onChange={handleToggle}
            />
          </div>
        )}
      </div>
      <div className="h-1 flex-1">
        {isChart ? <StratChart mode={mode} {...contentProps} /> : <StratTable {...contentProps} />}
      </div>
    </div>
  );
}
